import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchChantiers, deleteChantier } from '@/store/thunks/chantierThunks';
import type { Chantier } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { AddButton, FilterInput, Modal, DataTable, ConfirmModal, TableActions } from '@/components/ui';
import ChantierForm from '@/components/chantier/ChantierForm';
import ChantierMap from '@/components/chantier/ChantierMap';

function formatDate(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('fr-FR');
}

export default function ChantierPage() {
  const dispatch = useAppDispatch();
  const chantiers = useAppSelector((s: RootState) => s.chantier.chantiers);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Chantier | null>(null);
  const [toDelete, setToDelete] = useState<Chantier | null>(null);
  const [filterQuery, setFilterQuery] = useState('');

  useEffect(() => {
    dispatch(fetchChantiers());
  }, [dispatch]);

  const filteredChantiers = filterListByQuery(chantiers, filterQuery, ['code', 'nom', 'localisation']);

  const canCreate = isAdmin || checkPermission('CREATE_CHANTIER', authorities);
  const canUpdate = isAdmin || checkPermission('UPDATE_CHANTIER', authorities);
  const canDelete = isAdmin || checkPermission('DELETE_CHANTIER', authorities);

  const columns = [
    { key: 'code', label: 'Code' },
    { key: 'nom', label: 'Nom' },
    { key: 'date_d', label: 'Date début', render: (v: unknown) => formatDate(v) },
    { key: 'date_f', label: 'Date fin', render: (v: unknown) => formatDate(v) },
    { key: 'localisation', label: 'Localisation' },
  ];

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = () => {
    if (toDelete) {
      dispatch(deleteChantier(toDelete));
      setToDelete(null);
      setConfirmOpen(false);
    }
  };

  return (
    <Page title="Chantiers">
      <div className="space-y-6">
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-800">Carte des chantiers</h2>
          <ChantierMap chantiers={chantiers} className="w-full" />
        </section>
        <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2 mb-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">Liste des chantiers</h2>
            <FilterInput value={filterQuery} onChange={setFilterQuery} placeholder="Rechercher (code, nom, lieu)…" />
          </div>
          <AddButton label="Ajouter" disabled={!canCreate} onClick={() => { setEditing(null); setModalOpen(true); }} />
        </div>

        <DataTable<Chantier>
          columns={columns}
          data={filteredChantiers}
          keyField="id"
          emptyMessage="Aucun chantier"
          actions={(row) => (
            <TableActions
              onEdit={() => { setEditing(row); setModalOpen(true); }}
              onDelete={() => { setToDelete(row); setConfirmOpen(true); }}
              editDisabled={!canUpdate}
              deleteDisabled={!canDelete}
            />
          )}
        />
        </section>
      </div>

      <Modal open={modalOpen} onClose={handleCloseModal} title={editing ? 'Modifier le chantier' : 'Nouveau chantier'}>
        <ChantierForm chantier={editing ?? undefined} onClose={handleCloseModal} onSuccess={handleCloseModal} />
      </Modal>

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteConfirm} message="Voulez-vous supprimer ce chantier ?" />
    </Page>
  );
}
